import { useCallback, useEffect, useRef } from "react";

export const DOUBLE_TAP_WINDOW_MS = 280;

/**
 * Tells a double tap from a single one, per target key (a message id). Call the returned function
 * from the target's click handler.
 *
 * A single tap's action waits out the window, so it doesn't fire on the first half of a double tap
 * (tapping a photo twice to heart it shouldn't also open it). Where a single tap means nothing,
 * leave `onSingle` out and nothing waits.
 */
export function useDoubleTap(windowMs: number = DOUBLE_TAP_WINDOW_MS) {
  const lastTapRef = useRef<{ key: string; at: number } | null>(null);
  const singleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearSingle = () => {
    if (singleTimerRef.current) clearTimeout(singleTimerRef.current);
    singleTimerRef.current = null;
  };
  useEffect(() => clearSingle, []);

  return useCallback(
    (key: string, onDouble: () => void, onSingle?: () => void) => {
      const now = Date.now();
      const last = lastTapRef.current;
      clearSingle();
      if (last && last.key === key && now - last.at < windowMs) {
        lastTapRef.current = null;
        onDouble();
        return;
      }
      lastTapRef.current = { key, at: now };
      if (onSingle) singleTimerRef.current = setTimeout(onSingle, windowMs);
    },
    [windowMs],
  );
}
